/**
 * TV remote control: arrow keys move focus between elements the way a D-pad
 * should, OK activates, Back navigates back.
 *
 * This is what makes PoKu usable on the TVs that cannot install an APK — LG
 * webOS 22+, Samsung Tizen 2019+, and any Android/iOS box that only has a
 * browser. Those platforms give you a full-screen web app and a remote, and
 * nothing else: no pointer, no hover, no click. Without this layer the site is a
 * page of unclickable posters.
 *
 * Deliberately opt-in. Arrow keys are already spoken for on a desktop (the video
 * player seeks with them, the search field edits text with them), so spatial
 * focus is only armed when a TV user agent is seen or ?tv=1 is in the URL.
 * `spatial.js` holds the geometry; this file is the browser plumbing.
 */

import { FOCUSABLE_SELECTOR, spatialNext, toRects } from "./spatial.js";

/**
 * TV browsers and TV remote key codes are inconsistent, so match generously:
 * the string tests catch webOS/Tizen/Bravia/VIDAA/Chromecast-with-Google-TV
 * browsers, and the numeric map accepts both the DOM arrow codes (37-40) and the
 * Android DPAD codes (19-22) that many TV WebKit builds actually send, plus
 * Tizen/Samsung's non-standard OK (10009).
 */
const TV_UA =
  /Web0S|webOS|Tizen|SmartTV|BRAVIA|NetCast|HbbTV|VIDAA|GoogleTV|Android TV|AFT[A-Z0-9]{2,}|Roku|CrKey|Viera|aqua|PhilipsTV|Hisense/i;

export const DIRECTIONAL_KEYS = {
  ArrowLeft: "left",
  ArrowRight: "right",
  ArrowUp: "up",
  ArrowDown: "down",
  // Some TV WebKit builds report the physical key without the "Arrow" prefix.
  Left: "left",
  Right: "right",
  Up: "up",
  Down: "down",
  37: "left",
  38: "up",
  39: "right",
  40: "down",
  // Android DPAD_* as delivered by Tizen and Fire TV browser builds.
  19: "up",
  20: "down",
  21: "left",
  22: "right",
};

/** OK / Enter / centre button. 10009 is Tizen/Samsung's non-standard Enter. */
export const SELECT_KEY_CODES = [13, 32, 10009];
/** Back / Exit / Menu — only acted on when there is somewhere to go back to. */
export const BACK_KEY_CODES = [4, 8, 27, 10027, 166];
const SELECT_KEYS = new Set(SELECT_KEY_CODES);
const BACK_KEYS = new Set(BACK_KEY_CODES);

/** How far off-screen a candidate may be and still count as a directional target. */
const EDGE_MARGIN_RATIO = 0.4;
const EDGE_MARGIN_MIN = 200;

export function isTvUserAgent(ua) {
  const agent = ua || (typeof navigator === "undefined" ? "" : navigator.userAgent || "");
  return TV_UA.test(agent);
}

/** ?tv=1 forces the remote layer on (useful for testing a TV layout on a laptop). */
export function tvModeForced() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("tv") === "1";
  } catch {
    return false;
  }
}

function isTextEntry(el) {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable === true
  );
}

function isRendered(el) {
  if (!el || el.disabled) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  const style = typeof window !== "undefined" ? window.getComputedStyle(el) : null;
  if (!style) return true;
  return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
}

/**
 * Every element focus may visit: rendered, not explicitly skipped by the app
 * (rail arrows and similar mouse-only affordances carry data-remote-skip), and
 * either on screen or just outside it. Including the off-screen ones is what
 * lets Right walk along a rail whose next card has not scrolled into view yet —
 * focus is then scrolled to it.
 */
function collectCandidates() {
  if (typeof document === "undefined") return [];
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  const mx = Math.max(EDGE_MARGIN_MIN, vw * EDGE_MARGIN_RATIO);
  const my = Math.max(EDGE_MARGIN_MIN, vh * EDGE_MARGIN_RATIO);

  return Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => !el.closest("[data-remote-skip]"))
    .filter(isRendered)
    .map((el) => ({ el, rect: el.getBoundingClientRect() }))
    .filter(({ rect }) => rect.width > 0 && rect.height > 0)
    .filter(
      ({ rect }) =>
        rect.right > -mx && rect.left < vw + mx && rect.bottom > -my && rect.top < vh + my,
    );
}

function focusElement(el) {
  if (!el) return;
  if (document.activeElement !== el) {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
  // Older TV WebKit has no :focus-visible and no smooth scrolling; both calls are
  // guarded so a missing option degrades to an instant jump.
  el.setAttribute("data-remote-focus", "1");
  try {
    el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  } catch {
    el.scrollIntoView(false);
  }
}

function clearRemoteFocus(root) {
  if (typeof document === "undefined") return;
  const marked = root || document;
  if (marked.querySelectorAll) {
    marked.querySelectorAll("[data-remote-focus]").forEach((el) => {
      el.removeAttribute("data-remote-focus");
    });
  }
}

/**
 * Arm the remote layer. Returns a teardown function; a no-op when not on a TV,
 * so main.jsx can call it unconditionally.
 *
 * @param {{force?: boolean}} [options] force ignores user-agent detection.
 */
export function installRemoteNav(options = {}) {
  if (typeof document === "undefined" || typeof window === "undefined") return () => {};
  if (!options.force && !isTvUserAgent() && !tvModeForced()) return () => {};

  document.documentElement.setAttribute("data-tv", "1");

  let focused = null;

  const setFocus = (el) => {
    if (focused && focused !== el) focused.removeAttribute("data-remote-focus");
    focused = el;
    focusElement(el);
  };

  const move = (dir) => {
    const items = collectCandidates();
    if (items.length === 0) return;
    const active = document.activeElement;
    let from = -1;
    if (active && active !== document.body) {
      from = items.findIndex(({ el }) => el === active || el.contains(active));
    }
    if (from < 0) {
      setFocus(items[0].el);
      return;
    }
    const next = spatialNext(
      toRects(items.map(({ rect }) => rect)),
      from,
      dir,
    );
    if (next >= 0) setFocus(items[next].el);
  };

  /** After an SPA route change nothing is focused; give focus back to the top. */
  const reassert = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const items = collectCandidates();
      if (items.length > 0) setFocus(items[0].el);
    }, 80);
  };

  // The home screen is empty when this module runs, so the first reassert finds
  // nothing to focus and the viewer is left with no focus ring at all — on a TV
  // that is a dead first keypress and no indication of where the cursor is.
  // Watch for content arriving and claim focus once. Skipped whenever something
  // is already focused, so a page that mutates constantly (progress ticks, image
  // loads) cannot keep resetting the timer.
  let reassertTimer = 0;
  const scheduleReassert = () => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    window.clearTimeout(reassertTimer);
    reassertTimer = window.setTimeout(reassert, 120);
  };
  const observer =
    typeof MutationObserver !== "undefined" ? new MutationObserver(scheduleReassert) : null;
  if (observer) observer.observe(document.body, { childList: true, subtree: true });

  const onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const dir = DIRECTIONAL_KEYS[event.key] || DIRECTIONAL_KEYS[event.keyCode];
    if (dir) {
      // Left/right must still move the caret inside the search field and the
      // live-TV filter; up/down are not used for text editing, so they navigate.
      if (isTextEntry(document.activeElement) && (dir === "left" || dir === "right")) return;
      event.preventDefault();
      move(dir);
      return;
    }

    const key = event.keyCode;
    if (SELECT_KEYS.has(key) || event.key === "Enter" || event.key === " ") {
      const el = document.activeElement;
      // Text fields keep their own Enter handling (search, filters) — only
      // buttons and links are activated here.
      if (el && (el.tagName === "BUTTON" || el.tagName === "A")) {
        event.preventDefault();
        el.click();
      }
      return;
    }

    if (BACK_KEYS.has(key) || event.key === "Escape") {
      if (isTextEntry(document.activeElement)) return;
      if (window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    }
  };

  // Cards navigate on click, which unmounts the old page; re-assert focus once
  // the new screen has rendered.
  const onClickCapture = () => {
    clearRemoteFocus(null);
    focused = null;
    reassert();
  };

  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("popstate", reassert);
  document.addEventListener("click", onClickCapture, true);

  reassert();

  return () => {
    window.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("popstate", reassert);
    document.removeEventListener("click", onClickCapture, true);
    if (observer) observer.disconnect();
    window.clearTimeout(reassertTimer);
    clearRemoteFocus(null);
    document.documentElement.removeAttribute("data-tv");
  };
}
